import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import cs from './locales/cs/translation.json';
import en from './locales/en/translation.json';
import de from './locales/de/translation.json';

// Fetch country code by IP before i18n initialization
let detectedLanguage: string | null = null;

interface IpApiResponse {
  country_code: string;
}

const detectLanguageByIP = async () => {
  // Skip if language is already in localStorage
  const stored = localStorage.getItem('i18nextLng');
  if (stored) {
    detectedLanguage = stored;
    return;
  }

  try {
    // Use ipapi.co free API to detect country by IP
    const response = await fetch('https://ipapi.co/json/', {
      signal: AbortSignal.timeout(3000) // 3s timeout
    });
    const data = await response.json() as IpApiResponse;
    const country = data.country_code; // CZ, DE, AT, CH, etc.

    // Map country to language
    const countryToLang: Record<string, string> = {
      'CZ': 'cs',
      'SK': 'cs', // Slovakia -> Czech
      'DE': 'de',
      'AT': 'de', // Austria -> German
      'CH': 'de', // Switzerland -> German (majority)
    };

    detectedLanguage = countryToLang[country] || 'en'; // Default to English
  } catch (error) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('IP geolocation failed, will use browser language', error);
    }
    detectedLanguage = null; // Fall back to browser language
  }
};

// Custom IP-based language detector
const ipLanguageDetector = {
  name: 'ipDetector',
  lookup: () => {
    return detectedLanguage || undefined;
  },
  cacheUserLanguage: () => {
    // No caching here, localStorage handles it
  },
};

// Register custom detector
const customDetector = new LanguageDetector();
customDetector.addDetector(ipLanguageDetector);

const initI18n = async () => {
  await detectLanguageByIP();

  await i18n
    .use(customDetector)
    .use(initReactI18next)
    .init({
      resources: {
        cs: { translation: cs },
        en: { translation: en },
        de: { translation: de },
      },
      fallbackLng: 'en',
      supportedLngs: ['cs', 'en', 'de'],
      detection: {
        order: ['localStorage', 'ipDetector', 'querystring', 'navigator'],
        caches: ['localStorage'],
      },
      interpolation: { escapeValue: false },
    });
};

void initI18n();

export default i18n;
